import { FC, useState, lazy, Suspense } from 'react';
import { Clock, Layers, Box } from 'lucide-react';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import { IFieldWorkout, TacticalPhase } from '../../../models/field-programming';
import { TacticalSchemaSnapshot } from '../../../models/tactical-schema';
import styles from './FieldWorkoutDetailModal.module.scss';

const TacticalBoardViewer = lazy(() => import('../TacticalBoard/TacticalBoardViewer'));

interface Props {
  workout: IFieldWorkout | null;
  onClose: () => void;
}

const PHASE_LABELS: Record<TacticalPhase, string> = {
  warmup: 'Riscaldamento',
  activation: 'Attivazione',
  athletic: 'Atletico',
  technical: 'Tecnica',
  situational: 'Situazionale',
  match: 'Partita',
  compensative: 'Compensativo',
  cool_down: 'Defaticamento',
};

const PHASE_COLORS: Record<TacticalPhase, string> = {
  warmup: '#f59e0b',
  activation: '#fb923c',
  athletic: '#ef4444',
  technical: '#8b5cf6',
  situational: '#6366f1',
  match: '#10b981',
  compensative: '#14b8a6',
  cool_down: '#0ea5e9',
};

const BLOCK_TYPE_LABELS: Record<string, string> = {
  sequential: 'Sequenziale',
  parallel: 'Parallelo',
  rotation: 'Rotazione',
  break: 'Pausa',
  physical: 'Fisico',
};

const FieldWorkoutDetailModal: FC<Props> = ({ workout, onClose }) => {
  const [openViewer, setOpenViewer] = useState<{ snapshot: TacticalSchemaSnapshot } | null>(null);

  if (!workout) return null;

  const totalExercises = workout.blocks.reduce(
    (sum, b) => sum + (b.exercises?.length ?? 0),
    0,
  );

  return (
    <>
    <GenericModalV2 isOpen={!!workout} onClose={onClose} title={workout.name} size="lg">
      <div className={styles.body}>
        {/* Header info */}
        <div className={styles.headerMeta}>
          <span className={styles.metaItem}>
            <Layers size={13} strokeWidth={2.5} />
            {workout.blocks.length} blocc{workout.blocks.length === 1 ? 'o' : 'hi'}
          </span>
          <span className={styles.metaItem}>
            <Box size={13} strokeWidth={2.5} />
            {totalExercises} eserciz{totalExercises === 1 ? 'io' : 'i'}
          </span>
          {workout.defaultDurationMinutes && (
            <span className={styles.metaItem}>
              <Clock size={13} strokeWidth={2.5} />~{workout.defaultDurationMinutes} min
            </span>
          )}
          {workout.sport && (
            <span className={styles.sportBadge}>{workout.sport}</span>
          )}
        </div>

        {workout.description && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Descrizione</p>
            <p className={styles.descriptionText}>{workout.description}</p>
          </div>
        )}

        {workout.tags.length > 0 && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Tag</p>
            <div className={styles.tagRow}>
              {workout.tags.map((t) => (
                <span key={t} className={styles.tag}>
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Blocks */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Blocchi</p>
          <div className={styles.blocksList}>
            {workout.blocks
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((b, i) => {
                const phaseColor = PHASE_COLORS[b.tacticalPhase];
                return (
                  <div key={i} className={styles.blockCard}>
                    <div
                      className={styles.blockColorStrip}
                      style={{ background: phaseColor }}
                    />
                    <div className={styles.blockBody}>
                      <div className={styles.blockHeader}>
                        <p className={styles.blockTitle}>
                          B{i + 1}. {b.snapshotName}
                        </p>
                        <div className={styles.blockBadges}>
                          <span
                            className={styles.phaseBadge}
                            style={{
                              background: phaseColor + '18',
                              color: phaseColor,
                              border: `1px solid ${phaseColor}30`,
                            }}
                          >
                            {PHASE_LABELS[b.tacticalPhase]}
                          </span>
                          <span className={styles.subTypeBadge}>
                            {BLOCK_TYPE_LABELS[b.blockType] ?? b.blockType}
                          </span>
                          {b.durationMinutes != null && (
                            <span className={styles.durationBadge}>
                              <Clock size={11} strokeWidth={2.5} />
                              {b.durationMinutes} min
                            </span>
                          )}
                        </div>
                      </div>

                      {b.exercises && b.exercises.length > 0 && (
                        <ul className={styles.exerciseList}>
                          {b.exercises
                            .slice()
                            .sort((a, c) => a.order - c.order)
                            .map((ex, j) => (
                              <li key={j} className={styles.exerciseItem}>
                                <span className={styles.exOrder}>{ex.order + 1}</span>
                                <span className={styles.exName}>
                                  {ex.customName ?? '(esercizio libreria)'}
                                </span>
                                <div className={styles.exMeta}>
                                  {ex.durationMinutes != null && (
                                    <span>{ex.durationMinutes} min</span>
                                  )}
                                  {ex.intensity != null && <span>int {ex.intensity}</span>}
                                  {ex.organization && <span>{ex.organization}</span>}
                                </div>
                              </li>
                            ))}
                        </ul>
                      )}

                      {b.tacticalSchemaSnapshot && (
                        <Suspense fallback={null}>
                          <button
                            type="button"
                            onClick={() => setOpenViewer({ snapshot: b.tacticalSchemaSnapshot! })}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
                            title="Apri lavagna"
                          >
                            <TacticalBoardViewer snapshot={b.tacticalSchemaSnapshot} compact />
                          </button>
                        </Suspense>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </GenericModalV2>

    {openViewer && (
      <Suspense fallback={null}>
        <GenericModalV2 isOpen onClose={() => setOpenViewer(null)} size="xl" Header={<p>Lavagna</p>}>
          <TacticalBoardViewer snapshot={openViewer.snapshot} />
        </GenericModalV2>
      </Suspense>
    )}
  </>
  );
};

export default FieldWorkoutDetailModal;
